import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAnalytics } from '../contexts/AnalyticsContext';
import PreAssessmentScreen from '../components/assessment/PreAssessmentScreen';
import { motion } from 'framer-motion';
import { containerVariants, itemVariants } from '../lib/animations';
import AssessmentLayout from '../components/assessment/AssessmentLayout';
import ChildAvatarSelector from '../components/assessment/ChildAvatarSelector';
import AnimatedDropdown from '../components/assessment/AnimatedDropdown';
import StepIndicator from '../components/assessment/StepIndicator';
import { useCreateChild } from '../hooks/useAssessment';
import type { ChildAvatarKey } from '../utils/childAvatars';
import type { Gender } from '../types/assessment.types';
import {
  ONBOARDING_STEPS,
  TOTAL_STEPS,
  V1_CATEGORIES,
  FLOW_PARAMS,
  FLOW_TYPES,
  startOnboarding,
} from '../constants/onboardingFlow';

const AGE_RANGES = [
  { label: '2 - 3', fromAge: 2, toAge: 3 },
  { label: '3 - 4', fromAge: 3, toAge: 4 },
  { label: '4 - 5', fromAge: 4, toAge: 5 },
];

const GENDER_OPTIONS: { label: string; value: Gender }[] = [
  { label: 'Boy', value: 'male' },
  { label: 'Girl', value: 'female' },
  { label: 'Prefer not to say', value: 'prefer_not_to_say' },
];


interface FormErrors {
  avatarKey?: string;
  nickname?: string;
  ageRange?: string;
}

export default function ChildDetailsPage() {
  const { track } = useAnalytics();
  const navigate = useNavigate();
  const createChild = useCreateChild();

  const [avatarKey, setAvatarKey] = useState<ChildAvatarKey | null>('cat');
  const [nickname, setNickname] = useState('');
  const [gender, setGender] = useState<Gender | ''>('prefer_not_to_say');
  const [selectedAgeRange, setSelectedAgeRange] = useState<number | ''>();
  const [errors, setErrors] = useState<FormErrors>({});
  const [createdChildId, setCreatedChildId] = useState<number | null>(null);

  function validate(): boolean {
    const newErrors: FormErrors = {};

    if (!avatarKey) {
      newErrors.avatarKey = 'Please choose an avatar';
    }
    if (!nickname.trim()) {
      newErrors.nickname = 'Please enter a nickname';
    }
    if (selectedAgeRange === undefined || selectedAgeRange === '') {
      newErrors.ageRange = 'Please select an age range';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit() {
    if (!validate()) return;
    if (createChild.isPending) return;

    const ageRange = AGE_RANGES[selectedAgeRange as number];

    try {
      const child = await createChild.mutateAsync({
        nickname: nickname.trim(),
        avatarKey: avatarKey!,
        fromAge: ageRange.fromAge,
        toAge: ageRange.toAge,
        gender: gender !== '' ? (gender as Gender) : undefined,
        location: undefined,
      });

      localStorage.setItem('childId', String(child.id));
      track('add_child_complete', { child_id: String(child.id) });
      startOnboarding();
      setCreatedChildId(child.id);
    } catch {
      // Error is handled by React Query — createChild.error will be set
    }
  }

  function handleStartCheckIn() {
    if (!createdChildId) return;
    const firstCategory = V1_CATEGORIES[0];
    navigate(
      `/assessment/${createdChildId}?${FLOW_PARAMS.CATEGORY}=${firstCategory.key}&${FLOW_PARAMS.FLOW_TYPE}=${FLOW_TYPES.ONBOARDING}&${FLOW_PARAMS.STEP_NUMBER}=${firstCategory.step}`
    );
  }

  function handleDoThisLater() {
    navigate('/home');
  }

  if (createdChildId !== null) {
    return (
      <PreAssessmentScreen
        onStartCheckIn={handleStartCheckIn}
        onDoThisLater={handleDoThisLater}
      />
    );
  }

  const header = (
    <div className="flex flex-col bg-white">
      {/* Top bar with back button and logo */}
      <div className="flex items-center justify-between px-6 py-4">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="w-6 h-6 flex items-center justify-center"
          aria-label="Go back"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="#1B2831" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <span className="font-bold text-xl text-[#1B2831]">Play Moments</span>
        <div className="w-6" />
      </div>

      {/* Step indicator */}
      <div className="px-[118px] pb-4">
        <StepIndicator
          currentStep={ONBOARDING_STEPS.CHILD_DETAILS}
          totalSteps={TOTAL_STEPS}
        />
      </div>
    </div>
  );

  return (
    <AssessmentLayout header={header} backgroundColor="#FFFFFF">
      <motion.div
        className="flex flex-col items-center py-6"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div variants={itemVariants} className="text-center mb-6">
          <h1 className="text-[32px] px-10 font-bold text-[#1B2831] tracking-[-1.28px] leading-none">
            Tell us about your little one
          </h1>
          <p className="text-sm text-[#323B46] mt-6">
            A few details help us find the right sparks and build a learning plan that supports every next step.
          </p>
        </motion.div>

        {/* Avatar + Nickname (combined row) */}
        <motion.div variants={itemVariants} className="w-full mb-6">
          <label className="block text-sm font-bold text-[#1B2831] mb-2">
            Child&apos;s avatar and nickname*
          </label>
          <div className="flex gap-2.5 items-start">
            <ChildAvatarSelector
              selected={avatarKey}
              onSelect={(key) => {
                setAvatarKey(key);
                setErrors((prev) => ({ ...prev, avatarKey: undefined }));
              }}
              error={errors.avatarKey}
            />
            <div className="flex-1 min-w-0">
              <input
                type="text"
                value={nickname}
                onChange={(e) => {
                  setNickname(e.target.value);
                  setErrors((prev) => ({ ...prev, nickname: undefined }));
                }}
                placeholder="Enter nickname"
                className={`
                  w-full h-12 px-4 rounded-lg border text-sm text-[#1B2831]
                  placeholder:text-[#4D5762] focus:outline-none focus:border-[#01A7C8]
                  ${errors.nickname ? 'border-red-500' : 'border-[#A9B7BE]'}
                `}
              />
              {errors.nickname && (
                <p className="mt-1 text-sm text-red-600">{errors.nickname}</p>
              )}
            </div>
          </div>
        </motion.div>

        {/* Gender dropdown */}
        <motion.div variants={itemVariants} className="w-full mb-6">
          <label className="block text-sm font-bold text-[#1B2831] mb-2">
            Gender
          </label>
          <AnimatedDropdown
            options={GENDER_OPTIONS.map((opt) => ({ label: opt.label, value: opt.value }))}
            value={gender || undefined}
            onChange={(val) => setGender(val as Gender)}
            placeholder="Select gender"
          />
        </motion.div>

        {/* Age range dropdown */}
        <motion.div variants={itemVariants} className="w-full mb-8">
          <label className="block text-sm font-bold text-[#1B2831] mb-2">
            Age range*
          </label>
          <AnimatedDropdown
            options={AGE_RANGES.map((range, index) => ({ label: range.label, value: index }))}
            value={selectedAgeRange}
            onChange={(val) => {
              setSelectedAgeRange(val as number);
              setErrors((prev) => ({ ...prev, ageRange: undefined }));
            }}
            placeholder="Select age range"
            error={!!errors.ageRange}
          />
          {errors.ageRange && (
            <p className="mt-1 text-sm text-red-600">{errors.ageRange}</p>
          )}
        </motion.div>

        <motion.div variants={itemVariants} className="w-full mt-2">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={createChild.isPending}
            className="w-full h-12 rounded-lg bg-[#01A7C8] text-white font-black text-xs uppercase tracking-[0.72px] disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
          >
            {createChild.isPending ? 'Creating...' : 'let’s personalise your sparks'}
          </button>
          {createChild.isError && (
            <p className="mt-2 text-sm text-red-600 text-center">
              {createChild.error.message}
            </p>
          )}
        </motion.div>
      </motion.div>
    </AssessmentLayout>
  );
}
